import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { internalLinkTarget } from "@/lib/internal-link-target";
import { companyPreviewPanelStore } from "@/stores/company-preview-panel-store";
import { cn } from "@/lib/utils";
import { CompanyDTO } from "@shaarp/common";
import { t } from "i18next";
import type { MouseEvent } from "react";
import { NavLink } from "react-router-dom";
import AvatarCompany from "../company/avatar-company";
import { buttonVariants } from "../ui/button";
import { Card, CardContent, CardHeader } from "../ui/card";
import DataTableCellCompanyContact from "./data-table-cell-company-contact";
import DataTableCellCompanyLinks from "./data-table-cell-company-links";

interface DataTableCompaniesCardProps {
  company: CompanyDTO;
  selected?: CompanyDTO;
  rowClassNames?: (company: CompanyDTO) => string;
}

export function DataTableCompaniesCard({
  company,
  selected,
  rowClassNames = () => "",
}: DataTableCompaniesCardProps) {
  const isSelected = selected && selected.id === company.id;
  const { isMobile } = useDeviceFormat();

  // On a phone the company is read in the bottom sheet, the list kept in view.
  const openOnPhone = (event: MouseEvent) => {
    if (!isMobile) return;
    event.preventDefault();
    companyPreviewPanelStore.open(company, [company]);
  };

  return (
    <Card
      className={cn(
        "mb-4 transition-colors hover:bg-muted/50",
        rowClassNames(company),
        isSelected && "ring-2 ring-primary"
      )}
    >
      <CardHeader className="pb-3">
        <div className="flex gap-3 items-start">
          <AvatarCompany
            className="shrink-0 w-12 h-12"
            company={company}
          />
          <div className="flex-1 min-w-0">
            <NavLink
              to={`/company/${company.id}`}
              className="block"
              target={internalLinkTarget()}
              onClick={openOnPhone}
            >
              <h3 className="text-base font-bold leading-tight truncate hover:text-primary">
                {company.name}
              </h3>
            </NavLink>
            {company.location.address && (
              <div className="mt-1 text-sm text-muted-foreground">
                <p className="truncate">{company.location.address.line1}</p>
                {/* Wraps rather than truncates: the city is what tells two
                    establishments of one company apart. */}
                <p className="break-words">
                  {company.location.address.zipCode}{" "}
                  {company.location.address.city}
                </p>
              </div>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0 space-y-3">
        <div className="flex justify-between items-center">
          <div className="flex gap-2 items-center">
            <DataTableCellCompanyContact company={company} />
          </div>
          <div className="flex gap-2 items-center">
            <DataTableCellCompanyLinks company={company} />
          </div>
        </div>

        <div className="pt-2">
          <NavLink
            to={`/company/${company.id}`}
            className={cn(
              buttonVariants({
                variant: "default",
                size: "sm",
              }),
              "w-full"
            )}
            target={internalLinkTarget()}
            onClick={openOnPhone}
          >
            {t("components.dataTableCompanies.columns.consult")}
          </NavLink>
        </div>
      </CardContent>
    </Card>
  );
}
